import type { ResidentCreateBody, ResidentItem } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import { useMutation, useQueryClient } from 'react-query'

type CreateResidentResponse = {
  result: ResidentItem
}

type UseCreateResidentProps = {
  onSuccess?: (resident: ResidentItem) => void
}

type UseCreateResidentReturn = {
  createResident: (data: ResidentCreateBody) => Promise<ResidentItem>
  isCreating: boolean
}

const RESIDENTS_QUERY_KEY = 'residents'

const useCreateResident = ({
  onSuccess
}: UseCreateResidentProps = {}): UseCreateResidentReturn => {
  const queryClient = useQueryClient()

  const createFunction = async (
    data: ResidentCreateBody
  ): Promise<CreateResidentResponse> =>
    postJSON({
      url: 'list/residents',
      method: 'POST',
      json: data
    })

  const handleError = (err: unknown) => {
    showToast({
      title: err as string,
      type: 'error'
    })
  }

  const { mutateAsync, isLoading } = useMutation<
    CreateResidentResponse,
    unknown,
    ResidentCreateBody,
    unknown
  >(['create-resident'], createFunction, {
    onSuccess: (response) => {
      // Invalidate react-query cache
      queryClient.invalidateQueries(RESIDENTS_QUERY_KEY)

      // Call custom success handler
      if (onSuccess && response?.result) {
        onSuccess(response.result)
      }
    },
    onError: handleError
  })

  const createResident = async (
    data: ResidentCreateBody
  ): Promise<ResidentItem> => {
    const response = await mutateAsync(data)
    return response.result
  }

  return {
    createResident,
    isCreating: isLoading
  }
}

export default useCreateResident
